import styles from './style.postcss';

import React, { PureComponent } from 'react';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import is from 'is_js';
import PropTypes from 'prop-types';
import { connect, Button, Grid, PageCard } from 'omni-common-ui';
import CardActionIcons from 'components/CardActionIcons';
import {
  actions as FeedbackActions,
  accessors as FeedbackAccessors,
} from 'data/Feedback';
import { actions as NotesActions, accessors as Notes } from 'containers/Student/Note';
import Item from './Item';

const FEEDBACK_INSTRUCTION =
  'Teacher\'s feedback and suggestions';

const FEEDBACK_ITEMS = [
  { label: 'Strengths', code: 'strengths' },
  { label: 'Weaknesses', code: 'weaknesses' },
  { label: 'Performance', code: 'performance' },
  { label: 'Action Plan', code: 'actionPlan' },
];

const INTERNAL_NOTE = {
  label: 'Behaviour & Participation',
};

const NOTE_DESCRIPTION = 'Parents and students cannot see Behaviour & Participation';

class Feedback extends PureComponent {
  constructor() {
    super();
    this.state = {
      feedback: this._getInitFeedbackItem(),
      isEditing: false,
      note: '',
    };
    this._onSaveFeedback = this._onSaveFeedback.bind(this);
    this._onDiscardFeedback = this._onDiscardFeedback.bind(this);
    this._onEdit = this._onEdit.bind(this);
  }

  componentDidMount() {
    this._setUpData(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this._setUpData(nextProps);
  }

  _setUpData(props) {
    const { feedback, hasSaved, note } = props;
    this.setState({
      feedback: hasSaved ? feedback : this._getInitFeedbackItem(),
      isEditing: ! hasSaved,
      note: is.existy(note) ? note.get('content') : '',
    });
  }

  _getInitFeedbackItem() {
    return FEEDBACK_ITEMS.map((item) => ({
      name: item.code,
      value: '',
    }));
  }

  _shouldSaveButtonEnabled() {
    return this.state.feedback.reduce(
      (pre, cur) => pre && (is.string(cur.value) && cur.value.length !== 0),
      true
    );
  }

  _onSaveFeedback() {
    const {
      fetchFeedback,
      fetchStudentNotes,
      submitFeedback,
      sessionPlanId,
      studentId,
      groupId,
    } = this.props;
    const payload = this.state.feedback.reduce((pre, cur) =>
      Object.assign(pre, { [cur.name]: cur.value })
    , {});

    Promise.all([submitFeedback(sessionPlanId, studentId, payload),
      this._submitStudentNote(studentId, {
        groupId,
        category: NotesActions.STUDENT_NOTE_CATEGORY_ATTENDANCE,
        content: this.state.note,
        entityId: sessionPlanId,
      })]
    ).then(() => {
      fetchFeedback(sessionPlanId, studentId);
      fetchStudentNotes(studentId);
    });
  }

  _submitStudentNote(studentId, payload) {
    const { note, submitStudentNote } = this.props;
    if ((is.existy(note) && payload.content === note.get('content')) ||
      payload.content.length === 0) {
      return;
    }
    const payloadToSubmit = Object.assign(payload,
      { studentNoteId: is.existy(note) ? note.get('id') : undefined });
    return submitStudentNote(studentId, payloadToSubmit);
  }

  _onDiscardFeedback() {
    const { feedback, note } = this.props;
    if (is.existy(feedback)) {
      this.setState({
        feedback: is.existy(feedback) ? feedback : this._getInitFeedbackItem(),
        note: is.existy(note) ? note.get('content') : '',
        isEditing: false,
      });
    }
  }

  _onItemChange(val, item) {
    const feedback = this.state.feedback.map((feedbackItem) =>
      (feedbackItem.name === item ?
      ({ name: item, value: val }) : feedbackItem)
    );
    this.setState({ feedback });
  }

  _onNoteChange(val) {
    this.setState({ note: val });
  }

  _onEdit() {
    this.setState({ isEditing: true });
  }

  render() {
    const noteClassName = classnames({
      [styles.Feedback_content_note]: ! this.state.isEditing,
    });
    return <Grid outerMargin>
      <Grid.Group>
        <PageCard>
          <PageCard.Heading className={styles.Feedback_header}>
            <h1 className={styles.Feedback_header_title}>
              {FEEDBACK_INSTRUCTION}
            </h1>
            { this.state.isEditing ? null :
            <CardActionIcons onEditClick={this._onEdit}
                hideDeleteIcon /> }
          </PageCard.Heading>
          <PageCard.Content className={styles.Feedback_content}>
            {
              this.state.feedback.map((item) => {
                const feedbackItem = FEEDBACK_ITEMS.find((template) => template.code === item.name);
                return <Item title={feedbackItem.label}
                    isEdit={this.state.isEditing}
                    value={item.value}
                    onChange={(val) => this._onItemChange(val, feedbackItem.code)} />;
              })
            }
            { (! this.state.isEditing && ! is.existy(this.props.note)) ? null :
            <Item isEdit={this.state.isEditing}
                isWithoutBottomLine
                description={NOTE_DESCRIPTION}
                className={noteClassName}
                title={INTERNAL_NOTE.label}
                onChange={(val) => this._onNoteChange(val)}
                value={this.state.note} /> }
          </PageCard.Content>
          {
            ! this.state.isEditing ? null :
            <Button.Container align="right" className={styles.Feedback_btns}>
              <Button type={Button.Type.primary}
                  disabled={! this._shouldSaveButtonEnabled()}
                  onClick={this._onSaveFeedback}>
                SAVE
              </Button>
              <Button type={Button.Type.default}
                  onClick={this._onDiscardFeedback}>
                DISCARD
              </Button>
            </Button.Container>
          }
        </PageCard>
      </Grid.Group>
    </Grid>;
  }
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    {},
    bindActionCreators(NotesActions, dispatch),
    bindActionCreators(FeedbackActions, dispatch),
  );
}

function mapStateToProps(state, ownProps) {
  const { sessionPlanId, studentId, mode } = ownProps;
  const note = Notes.getAttendanceNoteByStudentIdAndSessionId(state, studentId, sessionPlanId);
  return {
    sessionPlanId,
    studentId,
    mode,
    feedback: FeedbackAccessors.getFeebackItems(state, sessionPlanId, studentId),
    hasSaved: FeedbackAccessors.hasFeedbackSaved(state, sessionPlanId, studentId),
    note,
  };
}

Feedback.propTypes = {
  groupId: PropTypes.string,
  sessionPlanId: PropTypes.string,
  studentId: PropTypes.string,
  mode: PropTypes.string,
  feedback: PropTypes.object,
  note: PropTypes.object,
  fetchFeedbackIfNeeded: PropTypes.func,
  fetchFeedback: PropTypes.func,
  fetchStudentNotesIfNeeded: PropTypes.func,
  fetchStudentNotes: PropTypes.func,
  submitFeedback: PropTypes.func,
  submitStudentNote: PropTypes.func,
  hasSaved: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(Feedback);



// WEBPACK FOOTER //
// ./containers/Group/SessionReport/Feedback/index.jsx